/**
 * AttachmentPicker — email attachments: add (click or drag & drop), list, remove.
 * Used by the send-message dialog and the email template editor.
 *
 * Look:
 *   - no files yet → one slim dashed strip "צרפו קבצים או גררו לכאן"
 *   - with files   → a grid of file cards (type-colored tile, name, size, ✕) and a last
 *                    "+ הוספת קובץ" card; dropping files anywhere on the grid adds them
 *   - a thin size meter "X מתוך 9 MB" (orange near the limit)
 * Validation (per-file max, total max) is done here before calling onAdd — unchanged.
 *
 * @param {Array}    attachments  [{ id, originalName, fileSize, formattedSize?, mimeType? }]
 * @param {Function} onAdd        called with each File that passes the size checks
 * @param {Function} onRemove     called with an attachment id
 * @param {number}   maxFileSize  bytes per file (default 8MB)
 * @param {number}   maxTotalSize bytes in total (default 9MB)
 * @param {boolean}  disabled     no adding/removing (e.g. template not saved yet)
 * @param {boolean}  uploading    show the uploading state
 */
import React, { useRef, useState } from 'react';
import {
  PaperClipOutlined,
  PlusOutlined,
  LoadingOutlined,
  CloseOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  FileZipOutlined,
  FileOutlined,
  VideoCameraOutlined,
  SoundOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import classes from './AttachmentPicker.css';

// Icon + tile color per file type
const fileKind = (mimeType = '') => {
  const m = mimeType || '';
  if (m.startsWith('image/')) return { icon: <FileImageOutlined />, color: '#7C3AED' };
  if (m.startsWith('video/')) return { icon: <VideoCameraOutlined />, color: '#DB2777' };
  if (m.startsWith('audio/')) return { icon: <SoundOutlined />, color: '#0EA5E9' };
  if (m.includes('pdf')) return { icon: <FilePdfOutlined />, color: '#DC2626' };
  // spreadsheets before Word: .xlsx is "…officedocument.spreadsheetml…" (contains "document")
  if (m.includes('spreadsheet') || m.includes('excel')) return { icon: <FileExcelOutlined />, color: '#16A34A' };
  if (m.includes('word') || m.includes('document')) return { icon: <FileWordOutlined />, color: '#2563EB' };
  if (m.includes('zip') || m.includes('compressed')) return { icon: <FileZipOutlined />, color: '#D97706' };
  return { icon: <FileOutlined />, color: '#71717A' };
};

const formatBytes = (bytes) => {
  if (!bytes) return '0 KB';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const AttachmentPicker = ({
  attachments = [],
  onAdd,
  onRemove,
  maxFileSize = 8 * 1024 * 1024,
  maxTotalSize = 9 * 1024 * 1024,
  disabled = false,
  uploading = false,
}) => {
  const fileInputRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [error, setError] = useState(null);

  const totalSize = attachments.reduce((sum, a) => sum + (a.fileSize || 0), 0);
  const remainingSize = maxTotalSize - totalSize;
  const isNearLimit = totalSize > maxTotalSize * 0.9; // 90% of limit
  const usedPct = Math.min(100, Math.round((totalSize / maxTotalSize) * 100));

  const handleFileSelect = (files) => {
    setError(null);
    if (!files || files.length === 0) return;
    for (const file of files) {
      if (file.size > maxFileSize) {
        setError(`הקובץ ${file.name} גדול מדי (${formatBytes(file.size)}). מקסימום ${formatBytes(maxFileSize)}`);
        continue;
      }
      if (totalSize + file.size > maxTotalSize) {
        setError(`אין מספיק מקום לקובץ ${file.name}. נותרו ${formatBytes(remainingSize)}`);
        continue;
      }
      if (onAdd) onAdd(file);
    }
  };

  const handleInputChange = (e) => {
    handleFileSelect(Array.from(e.target.files));
    e.target.value = ''; // so the same file can be picked again
  };

  const dragProps = {
    onDragOver: (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) setIsDragOver(true);
    },
    onDragLeave: (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
    },
    onDrop: (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      if (!disabled) handleFileSelect(Array.from(e.dataTransfer.files));
    },
  };

  const openPicker = () => {
    if (!disabled && !uploading && fileInputRef.current) fileInputRef.current.click();
  };

  const hasFiles = attachments.length > 0;

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <span className={classes.title}>
          <PaperClipOutlined /> קבצים מצורפים{hasFiles ? ` (${attachments.length})` : ''}
        </span>
        <span className={classes.meter} data-warn={isNearLimit}>
          <span className={classes.meterBar}>
            <span className={classes.meterFill} style={{ width: `${usedPct}%` }} />
          </span>
          <span dir="ltr" className={classes.meterText}>
            {formatBytes(totalSize)} / {formatBytes(maxTotalSize)}
          </span>
        </span>
      </div>

      <input ref={fileInputRef} type="file" multiple onChange={handleInputChange} className={classes.fileInput} disabled={disabled} />

      {!hasFiles ? (
        // Empty: a slim drop strip
        <button
          type="button"
          className={classes.strip}
          data-drag={isDragOver}
          disabled={disabled}
          onClick={openPicker}
          {...dragProps}
        >
          {uploading ? <LoadingOutlined className={classes.stripIcon} /> : <PlusOutlined className={classes.stripIcon} />}
          <span className={classes.stripText}>{uploading ? 'מעלה…' : 'צרפו קבצים או גררו לכאן'}</span>
          <span className={classes.stripHint}>עד {formatBytes(maxFileSize)} לקובץ</span>
        </button>
      ) : (
        // With files: a grid of cards + an "add" card; the whole grid accepts drops
        <ul className={classes.grid} data-drag={isDragOver} {...dragProps}>
          {attachments.map((attachment) => {
            const kind = fileKind(attachment.mimeType);
            return (
              <li key={attachment.id} className={classes.card}>
                <span className={classes.tile} style={{ color: kind.color, background: `${kind.color}14` }}>
                  {kind.icon}
                </span>
                <span className={classes.meta}>
                  <span className={classes.fileName} title={attachment.originalName}>
                    {attachment.originalName}
                  </span>
                  <span dir="ltr" className={classes.fileSize}>
                    {attachment.formattedSize || formatBytes(attachment.fileSize)}
                  </span>
                </span>
                {!disabled && (
                  <button
                    type="button"
                    className={classes.removeBtn}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onRemove) onRemove(attachment.id);
                    }}
                    title="הסר קובץ"
                    aria-label={`הסר ${attachment.originalName}`}
                  >
                    <CloseOutlined />
                  </button>
                )}
              </li>
            );
          })}
          {!disabled && (
            <li>
              <button type="button" className={classes.addCard} onClick={openPicker} disabled={uploading}>
                {uploading ? <LoadingOutlined /> : <PlusOutlined />}
                <span>{uploading ? 'מעלה…' : 'הוספת קובץ'}</span>
              </button>
            </li>
          )}
        </ul>
      )}

      {error && (
        <div className={classes.error}>
          <WarningOutlined /> {error}
        </div>
      )}
    </div>
  );
};

export default AttachmentPicker;
